import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  AutomatedNotesScheduleOutputDto,
  DashboardDatasetConfigOutputDto,
} from '@kortxio/hub-api';
import { AdminCampaignRow } from 'features/admin-campaigns/helper';
import { setError } from 'features/error/slice';
import api from 'libs/api';
import { parseApiPayload, toApiError } from 'libs/api/helper';
import { AsyncThunkConfig } from 'store/types';

export const getAllAdminCampaigns = createAsyncThunk<
  AdminCampaignRow[],
  void,
  AsyncThunkConfig
>(
  'adminCampaigns/getAllAdminCampaigns',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.campaign.findCampaignsUsingAdminProjection();
      const payload = parseApiPayload(response) as
        | AdminCampaignRow[]
        | undefined;

      if (payload === undefined) {
        throw new Error('Unexpected empty response when loading campaigns.');
      }

      return payload;
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({
            message: apiError.message,
            uiMessage: apiError.uiMessage,
          })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const getAllAdminDatasets = createAsyncThunk<
  DashboardDatasetConfigOutputDto[],
  void,
  AsyncThunkConfig
>(
  'adminCampaigns/getAllAdminDatasets',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response =
        await api.dashboardDatasetConfigs.getAllDashboardDatasetConfigsByReportLevel(
          'CAMPAIGN'
        );
      const payload = parseApiPayload(response) as
        | DashboardDatasetConfigOutputDto[]
        | undefined;

      if (payload === undefined) {
        throw new Error('Unexpected empty response when loading datasets.');
      }

      return payload;
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({
            message: apiError.message,
            uiMessage: apiError.uiMessage,
          })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const getAllAdminSchedules = createAsyncThunk<
  AutomatedNotesScheduleOutputDto[],
  void,
  AsyncThunkConfig
>(
  'adminCampaigns/getAllAdminSchedules',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response =
        await api.automaticNoteSchedules.getAllAutomatedNoteSchedulesByReportLevel(
          'CAMPAIGN'
        );
      const payload = parseApiPayload(response) as
        | AutomatedNotesScheduleOutputDto[]
        | undefined;

      if (payload === undefined) {
        throw new Error('Unexpected empty response when loading schedules.');
      }

      return payload;
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({
            message: apiError.message,
            uiMessage: apiError.uiMessage,
          })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);
